import beneficiaryFiltersSignal, {
	setBeneficiaryFilters,
} from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Dropdown } from '@verclam/ui-kit';
import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';

const ASSIGNED_USER_PLACEHOLDER = 'Tous les référents';

export type AssignedUserOption = {
	readonly id: number;
	readonly label: string;
};

type Props = {
	readonly options: readonly AssignedUserOption[];
	readonly loading: boolean;
	readonly withLabel?: boolean;
};

/**
 * The scope ("Tous" / "Mes dossiers") and the agency directory are folded into one dropdown rather than
 * kept as two controls: both would write the same server parameter (`assignedUserId`), and a second
 * control reading that field back could only ever approximate which of the two is "active" — a specific
 * colleague picked from the directory is neither `null` nor the current user's id. "Tous" is not listed
 * as a selectable option: `Dropdown` already treats `value === null` as "nothing selected"
 * (`findOptionByValue`, `Dropdown/util/options.ts`), so it is expressed as the empty state — a
 * placeholder plus `showClear` to return to it.
 */
const AssignedUserFilter: React.FC<Props> = ({ options, loading, withLabel = false }) => {
	const currentFilters = useSignalValue(beneficiaryFiltersSignal);

	const handleAssignedUserChange = React.useCallback((assignedUserId: number | null) => {
		setBeneficiaryFilters(beneficiaryFiltersSignal.peek().setAssignedUserId(assignedUserId));
	}, []);

	return (
		<Dropdown<number>
			name="beneficiaryAssignedUser"
			label="Référent affecté"
			isLabelHidden={!withLabel}
			options={options}
			optionLabel="label"
			optionValue="id"
			value={currentFilters.assignedUserId}
			onChange={handleAssignedUserChange}
			placeholder={ASSIGNED_USER_PLACEHOLDER}
			showClear
			loading={loading}
			filter
			pt={fieldWithoutMarginStyles}
		/>
	);
};

export default AssignedUserFilter;
